import { useState, useEffect } from 'react'
import { apiFetch, ApiError } from '../lib/api'
import { openDocumentReader } from '../lib/documentReader'
import type { TaskQueueItem } from '../lib/tasks'
import { formatOfficerValue } from '../lib/officerFormat'
import { HelpDot } from './guidance/Guidance'
import Sentence from './Sentence'

/* Trade-duplicate question pane (R7 / WI-3, mig 125).
 *
 * The pipeline held back a create_trade because a trade with the same
 * stable keys (holder + instrument + units + amount) is already on the
 * record. The officer answers the MANDATORY question: same trade seen
 * again (the document corroborates the record trade), or a genuinely
 * new trade (book it separately). Backend outcomes are exactly
 * `same_trade` / `new_trade` — POST /api/tasks/{id}/resolve; anything
 * else is a 422 (trade_duplicate_question_handler).
 */

interface DrawerLite {
  title: string
  subtitle: string | null
  facts: Record<string, string | number | boolean>
}

interface IncomingClaim {
  dedup_key?: string
  document_id?: string
  /** The full create_trade payload the builder would have proposed. */
  incoming: Record<string, unknown>
}

/** Payload spellings folded onto the drawer-facts (Trade column) key —
 *  same map the backend applies in proposals/live_values.py. Without it
 *  the cash reading can never line up row-to-row, so it never diffs. */
const KEY_ALIASES: Record<string, string> = {
  consideration_amount: 'amount',
  consideration_currency: 'currency',
}

/** Payload keys that are plumbing, not a reading the officer compares. */
const HIDDEN_KEYS = new Set([
  'dedup_key',
  'source',
  'source_coordinate',
  'source_language',
  'depositary_id',
  'relevance',
  'evidence_instructions',
])

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

function isComparable(k: string, v: unknown): boolean {
  if (HIDDEN_KEYS.has(k)) return false
  if (k.endsWith('_id') && typeof v === 'string' && UUID_RE.test(v)) return false
  return v == null || typeof v !== 'object'
}

function show(v: unknown): string {
  if (v === null || v === undefined || v === '') return '—'
  // Display-only formatting (audit finding 6-minor): "8000.000000" reads as
  // "8,000" so equal readings never LOOK different. sameReading() keeps
  // comparing the raw values — the diff logic is untouched.
  return formatOfficerValue(String(v)).replace(/_/g, ' ')
}

function sameReading(a: unknown, b: unknown): boolean {
  if (a == null || b == null) return a == null && b == null
  const na = Number(a)
  const nb = Number(b)
  if (String(a).trim() !== '' && String(b).trim() !== '' && !Number.isNaN(na) && !Number.isNaN(nb))
    return na === nb
  return String(a) === String(b)
}

function errText(e: unknown): string {
  if (e instanceof ApiError) {
    const d = (e.body as { detail?: unknown } | undefined)?.detail
    if (typeof d === 'string') return d
    return `${e.statusText} (${e.status})`
  }
  return e instanceof Error ? e.message : 'request failed'
}

function readIncomings(data: TaskQueueItem['task_data']): IncomingClaim[] {
  if (!data || typeof data !== 'object') return []
  const raw = (data as Record<string, unknown>).incomings
  if (!Array.isArray(raw)) return []
  return raw.filter(
    (x): x is IncomingClaim =>
      !!x && typeof x === 'object' && typeof (x as IncomingClaim).incoming === 'object'
  )
}

/** Role-labeled context cells — the triple is never an unlabeled
 *  dash-chain (Ale design rule). Read from the incoming payload. */
function IdentityCells({ incoming }: { incoming: Record<string, unknown> }) {
  const s = (k: string) => {
    const v = incoming[k]
    return typeof v === 'string' && v ? v : null
  }
  const cells = [
    { role: 'Holder', value: s('holder_entity_name') },
    { role: 'Instrument', value: s('instrument_name') },
    { role: 'Issuer', value: s('issuer_entity_name') },
  ].filter((c): c is { role: string; value: string } => c.value !== null)
  if (cells.length === 0) return null
  return (
    <div
      data-testid="trade-dup-identity"
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '6px 22px',
        padding: '8px 12px',
        background: 'var(--paper, #f8f7f4)',
        border: '1px solid var(--line)',
        borderRadius: 8,
        marginBottom: 12,
      }}
    >
      {cells.map((c) => (
        <div key={c.role}>
          <div
            style={{
              fontSize: 'var(--text-12)',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: 0.4,
              color: 'var(--faint)',
            }}
          >
            {c.role}
          </div>
          <div style={{ fontSize: 'var(--text-12)', fontWeight: 600 }}>{c.value}</div>
        </div>
      ))}
    </div>
  )
}

export default function TradeDuplicatePane({
  task,
  onResolved,
}: {
  task: TaskQueueItem
  onResolved: () => void
}) {
  const incomings = readIncomings(task.task_data)
  const [record, setRecord] = useState<DrawerLite | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState<string | null>(null)

  // `null` = not loaded yet / could not be loaded. The ON RECORD side is
  // fund-scoped, and a task whose fund is not derivable yet cannot fetch it —
  // in which case the comparison MUST say so rather than render a column of
  // em-dashes beside a live "same trade / new trade" decision (atlas p5,
  // 2026-07-28: the officer was asked to answer with one side blank).
  const [recordUnavailable, setRecordUnavailable] = useState(false)
  useEffect(() => {
    setRecord(null)
    if (!task.fund_id || !task.target_id) {
      setRecordUnavailable(true)
      return
    }
    setRecordUnavailable(false)
    apiFetch<DrawerLite>(`/api/funds/${task.fund_id}/objects/trade/${task.target_id}`)
      .then(setRecord)
      .catch(() => setRecordUnavailable(true))
  }, [task.fund_id, task.target_id])

  async function answer(outcome: 'same_trade' | 'new_trade') {
    setBusy(true)
    setError(null)
    try {
      await apiFetch(`/api/tasks/${task.task_id}/resolve`, {
        method: 'POST',
        body: JSON.stringify({ outcome }),
      })
      setDone(
        outcome === 'same_trade'
          ? 'Recorded: same trade — the document now corroborates the trade already on record.'
          : 'Recorded: new trade — booked separately as its own confirmed trade.'
      )
    } catch (e) {
      setError(errText(e))
    } finally {
      setBusy(false)
    }
  }

  function openDoc(documentId: string) {
    openDocumentReader(documentId, {
      fundId: task.fund_id,
      focus: task.target_type === 'trade' && task.target_id ? `trade:${task.target_id}` : null,
      returnTo: window.location.pathname + window.location.search,
    })
  }

  if (done)
    return (
      <div className="vcard" style={{ flex: 1, alignSelf: 'flex-start', padding: 22 }}>
        <div style={{ fontSize: 'var(--text-13)', color: 'var(--verified)', fontWeight: 600 }}>
          ✓ {done}
        </div>
        <button className="vbtn pri" style={{ marginTop: 16 }} onClick={onResolved}>
          Next task →
        </button>
      </div>
    )

  if (
    incomings.some(
      (item) =>
        Array.isArray(item.incoming._payment_allocations) &&
        item.incoming._payment_allocations.length > 0
    )
  ) {
    return (
      <div
        className="vcard"
        style={{ flex: 1, padding: 22 }}
        data-testid="payment-allocation-review"
      >
        <b>Review the payment allocation</b>
        <p>
          The allocation is incomplete or unclear. Review the source and obtain the missing details
          before settlement is recorded.
        </p>
        <p>No new capital trade is created.</p>
        {incomings
          .filter((item) => item.document_id)
          .map((item, index) => (
            <button
              key={index}
              className="vbtn"
              onClick={() => openDoc(item.document_id as string)}
            >
              Open source document
            </button>
          ))}
      </div>
    )
  }

  const recordFacts = Object.entries(record?.facts ?? {}).filter(([k, v]) => isComparable(k, v))

  return (
    <div style={{ flex: 1, display: 'flex', gap: 16, alignItems: 'flex-start' }}>
      {/* context: the two trades side by side */}
      <div className="vcard" style={{ flex: 1, padding: 0, overflow: 'hidden' }}>
        <div style={{ padding: '13px 18px', borderBottom: '1px solid var(--line)' }}>
          <div style={{ fontSize: 'var(--text-13)', fontWeight: 700 }}>
            Is this the same trade, or a new one? <HelpDot k="deck.trade_duplicate" />
          </div>
          <div style={{ fontSize: 'var(--text-12)', color: 'var(--faint)', marginTop: 2 }}>
            a document asserts a trade that matches one already on the record — same holder,
            instrument and amount. Differences are highlighted.
          </div>
        </div>
        <div style={{ padding: '14px 18px' }}>
          {incomings[0] && <IdentityCells incoming={incomings[0].incoming} />}
          {incomings.map((inc, i) => {
            const proposed = Object.entries(inc.incoming)
              .map(([k, v]): [string, unknown] => [KEY_ALIASES[k] ?? k, v])
              .filter(([k, v]) => isComparable(k, v))
            const keys: string[] = []
            const seen = new Set<string>()
            for (const [k] of [...recordFacts, ...proposed]) {
              if (!seen.has(k)) {
                seen.add(k)
                keys.push(k)
              }
            }
            const recordMap = new Map(recordFacts)
            const proposedMap = new Map(proposed)
            const showGrid = !(recordUnavailable && !record)
            const diffFields = keys
              .filter((k) => {
                const rv = recordMap.get(k)
                const pv = proposedMap.get(k)
                return rv !== undefined && pv !== undefined && !sameReading(rv, pv)
              })
              .map((k) => k.replace(/_/g, ' '))
            return (
              <div key={inc.dedup_key ?? i} data-testid="trade-dup-compare">
                {incomings.length > 1 && (
                  <div
                    style={{
                      fontSize: 'var(--text-12)',
                      color: 'var(--faint)',
                      margin: '10px 0 4px',
                    }}
                  >
                    claim {i + 1} of {incomings.length}
                  </div>
                )}
                {showGrid && diffFields.length > 0 && (
                  <Sentence
                    className="sn-lead"
                    data-testid="trade-dup-lead"
                    meaning="pane.trade_duplicate_lead"
                    style={{ display: 'block', fontSize: 'var(--text-12)', marginBottom: 8 }}
                    values={{ count: diffFields.length, fields: diffFields }}
                  />
                )}
                {recordUnavailable && !record ? (
                  <div
                    data-testid="trade-dup-record-unavailable"

                    style={{
                      fontSize: 'var(--text-12)',

                      color: 'var(--gap)',

                      border: '1px solid var(--line)',

                      borderRadius: 6,

                      padding: '8px 12px',
                    }}
                  >
                    The trade already on record could not be loaded, so the two cannot be compared
                    here. Open the record item before answering — do not decide from the document
                    side alone.
                  </div>
                ) : (
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'max-content 1fr 1fr',
                      gap: '4px 16px',
                      fontSize: 'var(--text-12)',
                    }}
                  >
                    <span />
                    <span
                      style={{ fontSize: 'var(--text-12)', fontWeight: 600, color: 'var(--faint)' }}
                    >
                      ON RECORD
                    </span>
                    <span
                      style={{ fontSize: 'var(--text-12)', fontWeight: 600, color: 'var(--faint)' }}
                    >
                      NEWLY PROPOSED
                    </span>
                    {keys.map((k) => {
                      const rv = recordMap.get(k)
                      const pv = proposedMap.get(k)
                      const differs = rv !== undefined && pv !== undefined && !sameReading(rv, pv)
                      return (
                        <span key={k} style={{ display: 'contents' }}>
                          <span style={{ color: 'var(--muted)' }}>{k.replace(/_/g, ' ')}</span>
                          <span
                            className="mono"
                            data-testid={differs ? 'trade-dup-diff' : undefined}
                            data-key={k}
                            style={{
                              fontWeight: 600,
                              background: differs ? 'rgba(163,93,23,0.10)' : undefined,
                              color: differs ? '#a35d17' : 'var(--ink)',
                              borderRadius: 4,
                              padding: differs ? '0 4px' : undefined,
                            }}
                          >
                            {show(rv)}
                          </span>
                          <span
                            className="mono"
                            style={{
                              fontWeight: 600,
                              background: differs ? 'rgba(163,93,23,0.10)' : undefined,
                              color: differs ? '#a35d17' : 'var(--ink)',
                              borderRadius: 4,
                              padding: differs ? '0 4px' : undefined,
                            }}
                          >
                            {show(pv)}
                          </span>
                        </span>
                      )
                    })}
                  </div>
                )}
              </div>
            )
          })}
          {incomings.length === 0 && (
            <div style={{ fontSize: 'var(--text-12)', color: 'var(--faint)' }}>
              No incoming claim payload on this task.
            </div>
          )}
          <div style={{ marginTop: 12, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {incomings
              .filter((inc) => typeof inc.document_id === 'string')
              .map((inc, i) => (
                <button
                  key={inc.document_id}
                  className="vbtn"
                  data-testid="trade-dup-doc-chip"
                  style={{ fontSize: 'var(--text-12)' }}
                  onClick={() => openDoc(inc.document_id as string)}
                >
                  source document {incomings.length > 1 ? i + 1 : ''}
                </button>
              ))}
          </div>
        </div>
      </div>

      {/* actions */}
      <div style={{ width: 380, display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div className="vcard" style={{ padding: 18 }}>
          {error && (
            <div style={{ fontSize: 'var(--text-12)', color: 'var(--gap)', marginBottom: 10 }}>
              {error}
            </div>
          )}
          <div style={{ fontSize: 'var(--text-12)', color: 'var(--muted)', marginBottom: 12 }}>
            Your answer is mandatory — the held document stays parked until you decide.
          </div>
          <button
            className="vbtn pri"
            data-testid="trade-dup-same"
            disabled={busy}
            style={{ width: '100%' }}
            onClick={() => answer('same_trade')}
          >
            Same trade — corroborates
          </button>
          <div style={{ fontSize: 'var(--text-12)', color: 'var(--faint)', margin: '4px 0 12px' }}>
            The document is another sighting of the trade on record; it attaches as corroborating
            evidence. Nothing new is booked.
          </div>
          <button
            className="vbtn"
            data-testid="trade-dup-new"
            disabled={busy}
            style={{ width: '100%' }}
            onClick={() => answer('new_trade')}
          >
            New trade — book separately
          </button>
          <div style={{ fontSize: 'var(--text-12)', color: 'var(--faint)', marginTop: 4 }}>
            A genuinely distinct trade (e.g. a second call of the same size); it is booked and
            confirmed as its own trade.
          </div>
        </div>
      </div>
    </div>
  )
}
